import {NgModule} from '@angular/core';
import {provideRouter, RouterModule, Routes, withComponentInputBinding} from '@angular/router';
import {AuthGuard} from './account/auth-guard';
import {HomeComponent} from './home/home.component';
import {TeacherGuard} from './teacher/teacher-guard';
import {StudentGuard} from './student/student-guard';
import {HomeGuard} from './home/home-guard';
import {TeacherHomeComponent} from './teacher/home/teacher-home.component';
import {StudentHomeComponent} from './student/home/student-home.component';
import {TeacherSignupComponent} from './teacher/teacher-signup/teacher-signup.component';
import {TeacherSignupGuard} from './teacher/teacher-signup/teacher-signup-guard';
import {MockupsComponent} from './mockups/mockups.component';
import {VideoMeetingMockupComponent} from './mockups/video-meeting-mockup/video-meeting-mockup.component';
import {GenericOutletComponent} from './generic-outlet/generic-outlet.component';
import {RedirectComponent} from './redirect/redirect.component';
import {AdminGuard} from './admin/admin-guard';
import {DocumentComponent} from './document/document.component';
import {TimetableComponent} from './timetable/timetable.component';
import {CampaignLandingComponent} from './account/campaign-landing/campaign-landing.component';
import {VideoPlayerComponent} from './teacher/video/player/video-player.component';
import {PublicNotFoundComponent} from './account/public-not-found/public-not-found.component';
import {RedirectUsertypeComponent} from './redirect-usertype/redirect-usertype.component';
import {LoginV2Component} from './login/login-v2.component';
import {HandleFederatedLoginComponent} from './login/handle-federated-login/handle-federated-login.component';
import {CanDeactivateGuard} from './events/session/guards/can-component-deactivate-guard';

const routes: Routes = [
  {
    path: 'redirect', component: GenericOutletComponent, children: [
      {path: '**', component: RedirectComponent}
    ]
  },
  {path: 'token', component: HandleFederatedLoginComponent},
  {
    path: 'redirect-usertype', component: GenericOutletComponent, children: [
      {path: '**', component: RedirectUsertypeComponent}
    ]
  },
  {
    path: 'videos/fullscreen', component: GenericOutletComponent, children: [
      {path: ':uuid/player', component: VideoPlayerComponent},
    ]
  },
  {path: 'join/:campaign', component: CampaignLandingComponent},
  {path: 'timetable', component: TimetableComponent},
  {path: 'redirect/:path1', component: RedirectComponent},
  {path: 'document/:documentId', component: DocumentComponent},
  {path: 'login/reset-password', component: LoginV2Component, data: {page: 'reset-password'}},
  {path: 'login/account-created', component: LoginV2Component, data: {page: 'account-created'}},
  {path: 'login/password', component: LoginV2Component, data: {page: 'existing-user-password'}},
  {path: 'login/error', component: LoginV2Component, data: {page: 'auth-error'}},
  {path: 'login', component: LoginV2Component, data: {page: 'auth-method'}},
  {path: 'login/token', component: HandleFederatedLoginComponent},
  {path: 'home', canActivate: [AuthGuard, HomeGuard], component: HomeComponent},
  {path: 'teacher/signup', canActivate: [TeacherSignupGuard], component: TeacherSignupComponent},

  {
    path: 'mockups', component: MockupsComponent,
    children: [
      {path: 'video', component: VideoMeetingMockupComponent},
      {path: '**', redirectTo: 'video'}
    ]
  },
  {
    path: 'admin', canActivate: [AuthGuard, AdminGuard], component: TeacherHomeComponent,
    loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),

  },
  {
    path: 'session/:id', canActivate: [AuthGuard],
    children: [
      {
        path: 'join',
        canActivate: [AuthGuard, StudentGuard],
        canDeactivate: [CanDeactivateGuard],
        loadComponent: () => import('./events/session/student-event-join/student-event-join.component').then((m) => m.StudentEventJoinComponent),
      },
      {
        path: 'start',
        canActivate: [AuthGuard, TeacherGuard],
        canDeactivate: [CanDeactivateGuard],
        loadComponent: () => import('./events/session/teacher-event-start/teacher-event-start.component').then((m) => m.TeacherEventStartComponent),
      },
    ]
  },
  {
    path: 'teacher/events/:id/start', canActivate: [AuthGuard, TeacherGuard],
    loadComponent: () => import('./events/session/teacher-event-start/teacher-event-start.component').then((m) => m.TeacherEventStartComponent),
  },
  {
    path: 'teacher', canActivate: [AuthGuard, TeacherGuard], component: TeacherHomeComponent,
    loadChildren: () => import('./teacher/teacher-module').then((m) => m.TeacherModule),
  },

  {
    path: 'student', canActivate: [StudentGuard], component: StudentHomeComponent,
    loadChildren: () => import('./student/student.module').then((m) => m.StudentModule),
  },
  {
    path: 'demo',
    loadComponent: () => import('./demo/demo.component').then((m) => m.DemoComponent),
  },
  {path: '404', component: PublicNotFoundComponent},
  {path: '**', redirectTo: '/home'},
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {useHash: false})],
  exports: [RouterModule],
  providers: [
    provideRouter(routes, withComponentInputBinding()), // Enable input binding
  ],
})
export class AppRoutingModule {
}
